"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type Check = Pick<ValidityState, "valid" | "valueMissing" | "typeMismatch" | "tooShort" | "tooLong" | "patternMismatch" | "rangeUnderflow" | "rangeOverflow" | "stepMismatch" | "badInput">;

/** Which message (a key of `forms.validation`) says what is wrong with a field, from the browser's own check: empty, wrong kind (email, web address), too short, too long, wrong shape, out of range. `null` when the field is fine. */
export function validationKey(v: Check, type: string): string | null {
  if (v.valid) return null;
  if (v.valueMissing) return "required";
  if (v.typeMismatch) return type === "email" ? "email" : type === "url" ? "url" : "pattern";
  if (v.tooShort) return "tooShort";
  if (v.tooLong) return "tooLong";
  if (v.rangeUnderflow) return "rangeUnderflow";
  if (v.rangeOverflow) return "rangeOverflow";
  if (v.patternMismatch) return "pattern";
  if (v.badInput || v.stepMismatch) return "number";
  return "invalid";
}

/** Fields outside a `<form>` that get the same live marks, messages and hints (autosaved settings pages): inside an element carrying this attribute. */
const LIVE_FIELDS = "[data-live-fields]";

const isChoice = (el: Element): el is HTMLInputElement => el instanceof HTMLInputElement && ["checkbox", "radio", "file"].includes(el.type);

/** A radio button stands for its whole group: the first of the group carries the mark and the message. */
function head(el: Field): Field {
  if (!(el instanceof HTMLInputElement) || el.type !== "radio" || !el.form || !el.name) return el;
  const first = el.form.elements.namedItem(el.name);
  return first instanceof RadioNodeList && first[0] instanceof HTMLInputElement ? first[0] : el;
}

/** Whether the person has put something in the field: text typed, a box ticked, a file chosen. */
const filled = (el: Field) => (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio") ? (el.type === "radio" && el.form && el.name ? [...el.form.querySelectorAll<HTMLInputElement>(`input[type=radio]`)].some((r) => r.name === el.name && r.checked) : el.checked) : el instanceof HTMLInputElement && el.type === "file" ? !!el.files?.length : el.value !== "");

const isField = (el: Element | null): el is Field => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement;
const boxOf = (el: Field) => document.getElementById(`${el.id}-error`);

/** Where a message goes: right after the field, or after the whole box when the field sits inside one (the phone field's flag and number), so it never lands inside the bordered box. */
const anchorOf = (el: Field): Element => el.closest(".PhoneInput")?.parentElement ?? (isChoice(el) ? el.closest("label") ?? el : el);

/** Whether a parent lays its children side by side: a flex row, or an element marked `data-field-row` (a grid row of fields). A message placed inside such a parent would become one more item of the row and squeeze the field. */
export function isFieldRow(style: { display: string; flexDirection: string }, marked: boolean): boolean {
  return marked || (/^(inline-)?flex$/.test(style.display) && style.flexDirection.startsWith("row"));
}

/** Where a message goes: after the anchor, or after the whole row when the anchor sits in one (a phone beside its label picker and remove button). */
const after = (el: Field): Element => {
  const anchor = anchorOf(el);
  const parent = anchor.parentElement;
  return parent && isFieldRow(getComputedStyle(parent), parent.hasAttribute("data-field-row")) ? parent : anchor;
};

function ensureId(el: Field) {
  if (!el.id) el.id = `field-${el.name || Math.random().toString(36).slice(2, 8)}`;
}

function clear(el: Field) {
  boxOf(el)?.remove();
  mark(el, null);
  el.removeAttribute("aria-invalid");
  const rest = (el.getAttribute("aria-describedby") ?? "").split(" ").filter((id) => id && id !== `${el.id}-error`).join(" ");
  if (rest) el.setAttribute("aria-describedby", rest); else el.removeAttribute("aria-describedby");
}

function show(el: Field, text: string, tag?: string) {
  ensureId(el);
  let box = boxOf(el);
  if (!box) {
    box = document.createElement("p");
    box.id = `${el.id}-error`;
    box.setAttribute("role", "alert");
    after(el).insertAdjacentElement("afterend", box);
  }
  box.className = `flex items-center gap-1.5 text-xs font-medium ${tag ? "text-destructive" : "text-warning"}`;
  box.replaceChildren();
  if (tag) { const badge = document.createElement("span"); badge.className = "rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning"; badge.textContent = tag; box.append(badge); }
  box.append(text);
  el.setAttribute("aria-invalid", "true");
  const described = (el.getAttribute("aria-describedby") ?? "").split(" ").filter(Boolean);
  if (!described.includes(box.id)) el.setAttribute("aria-describedby", [...described, box.id].join(" "));
}

const CHECK = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>';
const BANG = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v5M12 16h.01"/></svg>';
type State = "valid" | "invalid" | "missing";
const TONE: Record<State, string> = { valid: "text-success", invalid: "text-destructive", missing: "text-warning" };
const CROSS = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>';

/** What carries the mark: the field itself, or, for a hidden value of a drop-down list, the button that shows the choice. */
function markTarget(el: Field): HTMLElement | null {
  if (el instanceof HTMLInputElement && el.type === "hidden") { const next = el.nextElementSibling; return next instanceof HTMLButtonElement && next.type === "button" && (next.hasAttribute("aria-haspopup") || next.getAttribute("role") === "combobox") ? next : null; }
  return marked(el) && head(el) === el ? el : null;
}

const ICON: Record<State, string> = { valid: CHECK, invalid: CROSS, missing: BANG };

const marked = (el: Field) => !el.disabled && (isChoice(el) ? el.offsetParent !== null : el.offsetHeight > 20) && (isChoice(el) || el.tagName === "TEXTAREA" || (el instanceof HTMLInputElement && !["hidden", "checkbox", "radio", "file", "submit", "button", "range", "color", "search"].includes(el.type)));

/** Removes the mark (and the room made for it) from a field. */
function clearMark(el: HTMLElement, holder: HTMLElement) {
  holder.querySelector(`[data-field-mark="${el.id}"]`)?.remove();
  el.style.paddingInlineEnd = "";
  if (el instanceof HTMLButtonElement && el.firstElementChild instanceof HTMLElement) el.firstElementChild.style.marginInlineEnd = "";
}

/** Puts `span` over the end of a text field (or a button), making room for it. */
function placeMark(el: HTMLElement, holder: HTMLElement, span: HTMLElement) {
  holder.classList.add("relative");
  const box = el.getBoundingClientRect();
  const around = holder.getBoundingClientRect();
  const rtl = getComputedStyle(el).direction === "rtl";
  const gap = el.dataset.markEnd ? Number(el.dataset.markEnd) : el instanceof HTMLButtonElement ? 36 : 12;
  span.style.insetInlineEnd = `${(rtl ? box.left - around.left : around.right - box.right) + gap}px`;
  if (el instanceof HTMLButtonElement && el.firstElementChild instanceof HTMLElement) el.firstElementChild.style.marginInlineEnd = "1.75rem";
  span.style.top = `${box.top - around.top + box.height / 2}px`;
  el.insertAdjacentElement("afterend", span);
  if (!(el instanceof HTMLButtonElement) && !el.dataset.markEnd) el.style.paddingInlineEnd = "2rem";
}

/** The mark at the end of a field: green tick (valid), red cross (wrong), yellow exclamation (required and empty); `state` null removes it. */
function mark(field: Field, state: State | null) {
  const el = markTarget(field);
  const holder = el?.parentElement;
  if (!el || !holder) return;
  if (!el.id) el.id = `${field.id || "field"}-trigger`;
  clearMark(el, holder);
  if (!state) return;
  const span = document.createElement("span");
  span.dataset.fieldMark = el.id;
  span.setAttribute("aria-hidden", "true");
  span.innerHTML = ICON[state];
  if (isChoice(el)) { span.className = `pointer-events-none ms-1.5 inline-flex align-middle ${TONE[state]}`; el.insertAdjacentElement("afterend", span); return; }
  span.className = `pointer-events-none absolute -translate-y-1/2 ${TONE[state]}`;
  placeMark(el, holder, span);
}

/** A field's `data-hint` (what it must look like, with an example) shown under it from the start, before anything is typed, and kept in step if the hint changes (a country was picked). */
function hint(el: Field) {
  if (!el.dataset.hint) return;
  ensureId(el);
  let p = document.getElementById(`${el.id}-hint`);
  if (!p) {
    p = document.createElement("p");
    p.id = `${el.id}-hint`;
    p.className = "text-xs font-normal text-muted-foreground";
    after(el).insertAdjacentElement("afterend", p);
  }
  if (p.textContent !== el.dataset.hint) p.textContent = el.dataset.hint;
}

type Say = (key: string, values: { min: string | number; max: string | number }) => string;

function checkField(field: Field, say: Say, loud = true): boolean {
  const el = head(field);
  const key = el.willValidate ? validationKey(el.validity, el.type) : null;
  if (!key) { clear(el); ensureId(el); mark(el, filled(el) ? "valid" : null); return true; }
  if (key === "required") { clear(el); ensureId(el); if (loud) show(el, say(key, { min: "", max: "" })); mark(el, "missing"); return false; }
  const min = "minLength" in el && el.minLength > 0 ? el.minLength : (el as HTMLInputElement).min;
  const max = "maxLength" in el && el.maxLength > 0 ? el.maxLength : (el as HTMLInputElement).max;
  const text = say(key, { min, max });
  ensureId(el);
  show(el, el.dataset.error || (key === "pattern" && el.dataset.hint ? `${text} ${el.dataset.hint}` : text), say("errorTag", { min: "", max: "" }));
  mark(el, "invalid");
  return false;
}

/** For a field whose rule the browser cannot check itself (a phone number for its country, an IBAN): tells the validation layer whether the value is good, with the words to show when it is not. The layer then draws the mark and message like for any field. */
export function setFieldValidity(target: HTMLElement | null, ok: boolean, message: string) {
  // A phone field puts its id on a wrapper; the input to mark is inside it.
  const el = target && "setCustomValidity" in target ? (target as HTMLInputElement) : target?.querySelector("input");
  if (!el) return;
  el.setCustomValidity(ok ? "" : message);
  el.dataset.error = ok ? "" : message;
  el.dispatchEvent(new Event("fieldcheck", { bubbles: true }));
}

/** Writes a reason under the field called `name` of `form` (the server's refusal pointing at a field). `false` when the form has no such field. */
export function showFieldError(form: HTMLFormElement | null, name: string, text: string): boolean {
  const el = form?.elements.namedItem(name);
  if (!el || !isField(el as Element)) return false;
  show(el as Field, text);
  return true;
}

/** Mounted once for the whole application: every form, whoever built it, shows what is wrong under each wrong field, in the reader's language, instead of the browser's bubble. On send every wrong field gets its message (and the first is focused, unless the person is typing in a field); a field is checked when left and again as it is fixed, so its message goes away by itself. A field can bring its own wording in `data-error`. It uses the fields' own rules (`required`, `type`, `minLength`, `maxLength`, `min`, `max`, `pattern`), so a form is validated by declaring them. Renders nothing. */
export function FieldValidation() {
  const t = useTranslations("forms.validation");
  useEffect(() => {
    const say: Say = (key, values) => t(key, values);
    const inForm = (e: Event) => { const el = e.target as Element | null; return el && isField(el) && (el.form || el.closest(LIVE_FIELDS)) ? el : null; };
    let focusing = false;
    const onInvalid = (e: Event) => {
      const el = inForm(e);
      if (!el) return;
      e.preventDefault();
      checkField(el, say);
      if (focusing || isField(document.activeElement)) return;
      focusing = true;
      el.focus();
      setTimeout(() => { focusing = false; });
    };
    const onOut = (e: Event) => { const el = inForm(e); if (el) checkField(el, say); };
    const onIn = (e: Event) => { const el = inForm(e); if (el) checkField(el, say); };
    // Fields that arrive filled in (a saved profile) get their green tick without being touched.
    // React attaches __reactFiber$ to a node once hydrated; marking an unhydrated field would break hydration, so wait and retry.
    const hydrated = (el: Element) => Object.keys(el).some((k) => k.startsWith("__reactFiber"));
    const scan = () => {
      let waiting = false;
      document.querySelectorAll(`form input, form textarea, ${LIVE_FIELDS} input, ${LIVE_FIELDS} textarea`).forEach((el) => {
        if (isField(el) && el.dataset.hint) { if (hydrated(el)) hint(el); else waiting = true; }
        const target = isField(el) && (filled(el) || el.required) ? markTarget(el) : null;
        if (!isField(el) || !target || boxOf(el)) return;
        if (!hydrated(el)) { waiting = true; return; }
        ensureId(el);
        if (!target.parentElement?.querySelector(`[data-field-mark="${target.id}"]`)) checkField(el, say, false);
      });
      if (waiting) retry = window.setTimeout(later, 300);
    };
    let retry = 0;
    let frame = 0;
    const later = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(scan); };
    const watcher = new MutationObserver(later);
    watcher.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-hint"] });
    later();
    document.addEventListener("invalid", onInvalid, true);
    document.addEventListener("focusout", onOut);
    document.addEventListener("input", onIn);
    document.addEventListener("change", onIn);
    document.addEventListener("fieldcheck", onIn);
    return () => { watcher.disconnect(); cancelAnimationFrame(frame); clearTimeout(retry); document.removeEventListener("invalid", onInvalid, true); document.removeEventListener("focusout", onOut); document.removeEventListener("input", onIn); document.removeEventListener("change", onIn); document.removeEventListener("fieldcheck", onIn); };
  }, [t]);
  return null;
}
